import React, { useState, useEffect, useCallback } from 'react';
import { ethers, BrowserProvider, Contract, formatUnits, parseUnits } from 'ethers';

// --- CONFIGURATION ---
// Replace these with your actual deployed addresses on Base
const CONFIG = {
  chainId: 8453, // Base Mainnet (Use 84532 for Base Sepolia Testnet)
  converterAddress: "0x718dd623d9c354dcf812683763906e89a2e72609",
  usdTokenAddress: "0x7a737190e6912EaB930b3C70a3785682Ef2b95FF",
  rubTokenAddress: "0x871d44c09c89137e6c64f9c930eae9d55529c59d",
  decimals: 2 // Both tokens have 2 decimals
};

// --- MINIMAL ABIS ---
const CONVERTER_ABI = [
  "function getQuoteRubToUsd(uint256 amountRub) view returns (uint256)",
  "function getQuoteUsdToRub(uint256 amountUsd) view returns (uint256)",
  "function swapRubToUsd(uint256 amountRub) external returns (uint256)",
  "function swapUsdToRub(uint256 amountUsd) external returns (uint256)",
  "function oracle() view returns (address)"
];

const ERC20_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)"
];

const ORACLE_ABI = [
  "function price() view returns (uint256)"
];

export default function SwapApp() {
  // State
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  
  // Data
  const [balances, setBalances] = useState({ usd: "0", rub: "0" });
  const [allowances, setAllowances] = useState({ usd: "0", rub: "0" });
  const [oraclePrice, setOraclePrice] = useState("0"); // 18 decimals

  // Inputs
  const [amountUSD, setAmountUSD] = useState("");
  const [amountRUB, setAmountRUB] = useState("");
  const [quoteUSD, setQuoteUSD] = useState(""); // Quote for RUB input
  const [quoteRUB, setQuoteRUB] = useState(""); // Quote for USD input

  // Loading States
  const [isApproving, setIsApproving] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [status, setStatus] = useState("");

  // --- Connection ---
  const connectWallet = async () => {
    if (!window.ethereum) return alert("Please install MetaMask");
    try {
      const _provider = new BrowserProvider(window.ethereum);
      const accounts = await _provider.send("eth_requestAccounts", []);
      const _signer = await _provider.getSigner();
      
      const network = await _provider.getNetwork();
      if (Number(network.chainId) !== CONFIG.chainId) {
        alert(`Please switch network to Base (Chain ID: ${CONFIG.chainId})`);
        return;
      }

      setProvider(_provider);
      setSigner(_signer);
      setAccount(accounts[0]);
    } catch (err) {
      console.error(err);
      setStatus("Connection failed: " + err.message);
    }
  };

  // --- Data Fetching ---
  const fetchData = useCallback(async () => {
    if (!account || !provider) return;

    try {
      const usdContract = new Contract(CONFIG.usdTokenAddress, ERC20_ABI, provider);
      const rubContract = new Contract(CONFIG.rubTokenAddress, ERC20_ABI, provider);
      const converterContract = new Contract(CONFIG.converterAddress, CONVERTER_ABI, provider);
      
      // Get Oracle Address then Price
      const oracleAddress = await converterContract.oracle();
      const oracleContract = new Contract(oracleAddress, ORACLE_ABI, provider);

      const [usdBal, rubBal, usdAllow, rubAllow, price] = await Promise.all([
        usdContract.balanceOf(account),
        rubContract.balanceOf(account),
        usdContract.allowance(account, CONFIG.converterAddress),
        rubContract.allowance(account, CONFIG.converterAddress),
        oracleContract.price()
      ]);

      setBalances({
        usd: formatUnits(usdBal, CONFIG.decimals),
        rub: formatUnits(rubBal, CONFIG.decimals)
      });
      setAllowances({
        usd: formatUnits(usdAllow, CONFIG.decimals),
        rub: formatUnits(rubAllow, CONFIG.decimals)
      });
      const rawPrice = parseFloat(formatUnits(price, 18));
      setOraclePrice(rawPrice.toFixed(1));
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  }, [account, provider]);

  useEffect(() => {
    if (account) fetchData();
    const interval = setInterval(() => { if(account) fetchData(); }, 15000); // Poll every 15s
    return () => clearInterval(interval);
  }, [account, fetchData]);

  // --- Quote Logic ---
  const getRubQuote = async (val) => {
    setAmountUSD(val);
    if (!val || parseFloat(val) === 0) { setQuoteRUB(""); return; }
    try {
      const contract = new Contract(CONFIG.converterAddress, CONVERTER_ABI, provider);
      const quote = await contract.getQuoteUsdToRub(parseUnits(val, CONFIG.decimals));
      setQuoteRUB(formatUnits(quote, CONFIG.decimals));
    } catch (e) { console.error(e); }
  };

  const getUsdQuote = async (val) => {
    setAmountRUB(val);
    if (!val || parseFloat(val) === 0) { setQuoteUSD(""); return; }
    try {
      const contract = new Contract(CONFIG.converterAddress, CONVERTER_ABI, provider);
      const quote = await contract.getQuoteRubToUsd(parseUnits(val, CONFIG.decimals));
      setQuoteUSD(formatUnits(quote, CONFIG.decimals));
    } catch (e) { console.error(e); }
  };

  // --- Actions ---
  const approve = async (tokenType) => {
    setIsApproving(true);
    setStatus("Approving...");
    try {
      const tokenAddress = tokenType === 'usd' ? CONFIG.usdTokenAddress : CONFIG.rubTokenAddress;
      const contract = new Contract(tokenAddress, ERC20_ABI, signer);
      // Approve max uint256 for simplicity
      const tx = await contract.approve(CONFIG.converterAddress, ethers.MaxUint256);
      await tx.wait();
      setStatus("Approved!");
      fetchData();
    } catch (err) {
      console.error(err);
      setStatus("Approval failed");
    } finally {
      setIsApproving(false);
    }
  };

  const executeSwap = async (direction) => {
    setIsSwapping(true);
    setStatus("Swapping...");
    try {
      const contract = new Contract(CONFIG.converterAddress, CONVERTER_ABI, signer);
      let tx;
      
      if (direction === 'usdToRub') {
        const amountWei = parseUnits(amountUSD, CONFIG.decimals);
        tx = await contract.swapUsdToRub(amountWei);
      } else {
        const amountWei = parseUnits(amountRUB, CONFIG.decimals);
        tx = await contract.swapRubToUsd(amountWei);
      }

      await tx.wait();
      setStatus("Swap Confirmed!");
      setAmountUSD("");
      setAmountRUB("");
      setQuoteRUB("");
      setQuoteUSD("");
      fetchData();
    } catch (err) {
      console.error(err);
      setStatus("Swap failed: " + (err.reason || err.message));
    } finally {
      setIsSwapping(false);
    }
  };

  // --- Render Helpers ---
  const needsApproval = (amount, allowance) => {
    if (!amount) return false;
    return parseFloat(allowance) < parseFloat(amount);
  };
  
  const hasBalance = (amount, balance) => {
    if (!amount) return true;
    return parseFloat(balance) >= parseFloat(amount);
  };

  if (!account) {
    return (
      <div style={styles.container}>
        <button onClick={connectWallet} style={styles.button}>Connect MetaMask</button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={{color: '#333'}}>PMDT/pmUSD Swap</h2>
      
      <div style={styles.card}>
        <div style={styles.row}>
          <span>Current Average Price:</span>
          <strong>1 PMDT = {oraclePrice} pmUSD</strong>
        </div>
        <div style={styles.row}>
          <span>Status:</span>
          <strong>{status}</strong>
        </div>
      </div>

      <div style={styles.grid}>
        {/* CARD 1: USD -> RUB */}
        <div style={styles.card}>
          <h3>Swap pmUSD to PMDT</h3>
          <p>Balance: {balances.usd} pmUSD</p>
          
          <input 
            style={styles.input}
            type="number" 
            placeholder="Amount pmUSD" 
            value={amountUSD}
            onChange={(e) => getRubQuote(e.target.value)}
          />
          {quoteRUB && <p style={styles.quote}>Get approx: {quoteRUB} PMDT</p>}
          
          {!hasBalance(amountUSD, balances.usd) ? (
             <button disabled style={{...styles.button, opacity: 0.5}}>Insufficient Balance</button>
          ) : needsApproval(amountUSD, allowances.usd) ? (
            <button 
              onClick={() => approve('usd')} 
              disabled={isApproving}
              style={styles.button}
            >
              {isApproving ? "Approving..." : "Approve pmUSD"}
            </button>
          ) : (
            <button 
              onClick={() => executeSwap('usdToRub')} 
              disabled={isSwapping || !amountUSD}
              style={styles.button}
            >
              {isSwapping ? "Swapping..." : "Confirm Swap"}
            </button>
          )}
        </div>

        {/* CARD 2: RUB -> USD */}
        <div style={styles.card}>
          <h3>Swap PMDT to pmUSD</h3>
          <p>Balance: {balances.rub} PMDT</p>
          
          <input 
            style={styles.input}
            type="number" 
            placeholder="Amount PMDT" 
            value={amountRUB}
            onChange={(e) => getUsdQuote(e.target.value)}
          />
          {quoteUSD && <p style={styles.quote}>Get approx: {quoteUSD} pmUSD</p>}

          {!hasBalance(amountRUB, balances.rub) ? (
             <button disabled style={{...styles.button, opacity: 0.5}}>Insufficient Balance</button>
          ) : needsApproval(amountRUB, allowances.rub) ? (
            <button 
              onClick={() => approve('rub')} 
              disabled={isApproving}
              style={styles.button}
            >
              {isApproving ? "Approving..." : "Approve PMDT"}
            </button>
          ) : (
            <button 
              onClick={() => executeSwap('rubToUsd')} 
              disabled={isSwapping || !amountRUB}
              style={styles.button}
            >
              {isSwapping ? "Swapping..." : "Confirm Swap"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Enhanced Styles ---
const styles = {
  container: { 
    maxWidth: '600px', 
    margin: '60px auto', 
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', 
    padding: '24px',
    backgroundColor: '#111',
    color: '#fff',
    borderRadius: '16px',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.5)'
  },
  header: { textAlign: 'center', marginBottom: '30px', color: '#0052FF' }, // Base Blue
  grid: { display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }, // Stacked on mobile/desktop for simplicity
  card: { 
    border: '1px solid #333', 
    padding: '20px', 
    borderRadius: '12px', 
    background: '#1a1a1a', 
    marginBottom: '16px' 
  },
  row: { display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#aaa' },
  val: { color: '#fff', fontWeight: 'bold' },
  input: { 
    width: '100%', 
    padding: '16px', 
    fontSize: '24px', 
    margin: '10px 0', 
    borderRadius: '8px', 
    border: '1px solid #333', 
    background: '#000', 
    color: '#fff',
    outline: 'none',
    boxSizing: 'border-box' // Fixes padding width issues
  },
  button: { 
    width: '100%', 
    padding: '16px', 
    background: '#0052FF', 
    color: 'white', 
    border: 'none', 
    borderRadius: '8px', 
    cursor: 'pointer', 
    fontSize: '16px', 
    fontWeight: '600',
    marginTop: '8px',
    transition: 'opacity 0.2s'
  },
  quote: { fontSize: '12px', color: '#0052FF', textAlign: 'right', marginTop: '-5px', marginBottom: '15px' },
  status: { textAlign: 'center', marginTop: '20px', fontSize: '12px', color: '#666' }
};
